import { createCrudService } from '@/services/api/createCrudService'
import { api } from '@/services/api/AxiosBaseService'
import type { SingleResponse } from '@/types/api'

export type MedicalAppointmentStatus = 'AGUARDANDO CONFIRMAÇÃO' | 'CONFIRMADO' | 'CANCELADO'

export interface MedicalAppointment {
  id: string
  subscriberId: string
  providerId: string | null
  specialtyId: string | null
  appointmentDate: string
  appointmentTime: string
  doctorName: string | null
  status: MedicalAppointmentStatus
  attendedAt: string | null
  attendedBy: string | null
  customConfirmation: CustomConfirmation | null
  createdBy?: string | null
  confirmedBy?: string | null
  confirmedAt?: string | null
  createdAt: string
  subscriber?: { id: string; name: string; cpf: string; membershipCardNumber: string }
  provider?: { id: string; name: string }
  specialty?: { id: string; specialty: string }
  attendedByUser?: { id: string; name: string }
  creator?: { id: string; name: string } | null
  confirmer?: { id: string; name: string } | null
  formatted?: {
    appointmentDate: string
    appointmentTime: string
    createdAt?: string
    confirmedAt?: string
  }
}

export interface CustomConfirmation {
  clinicLaboratoryName: string
  confirmationDate: string
  confirmationTime: string
  address: string
  doctorName: string
  price: number
}

const base = createCrudService<MedicalAppointment>('/medical-appointments/appointments', {
  searchField: 'doctor_name',
})

export const MedicalAppointmentsService = {
  ...base,

  /** Busca de paciente (titulares e dependentes ativos) por nome, CPF ou código. */
  async subscriberOptions(q: string): Promise<{ id: string; name: string; cpf: string | null; type: string }[]> {
    const res = await api.get<{ data: { id: string; name: string; cpf: string | null; type: string }[] }>(
      '/medical-appointments/options/subscribers', { params: q ? { q } : undefined },
    )
    return res.data.data
  },

  /** Busca de credenciado — só os ACEITOS. */
  async providerOptions(q: string): Promise<{ id: string; name: string }[]> {
    const res = await api.get<{ data: { id: string; name: string }[] }>(
      '/medical-appointments/options/providers', { params: q ? { q } : undefined },
    )
    return res.data.data
  },

  async providerSpecialties(providerId: string): Promise<{ id: string; specialty: string }[]> {
    const res = await api.get<{ data: { id: string; specialty: string }[] }>(`/medical-appointments/options/providers/${providerId}/specialties`)
    return res.data.data
  },

  /** Confirma uma consulta pendente (exige horário e médico). */
  async confirm(id: string, payload: { appointment_time: string; doctor_name: string }): Promise<MedicalAppointment> {
    const res = await api.post<SingleResponse<MedicalAppointment>>(`/medical-appointments/appointments/${id}/confirm`, payload)
    return res.data.data
  },

  async confirmationPdf(id: string): Promise<string> {
    const res = await api.get<{ url: string }>(`/medical-appointments/appointments/${id}/confirmation-pdf`)
    return res.data.url
  },

  async createCustomConfirmation(
    id: string,
    payload: {
      clinic_laboratory_name: string
      confirmation_date: string
      confirmation_time: string
      address: string
      doctor_name: string
      price: number
    },
  ): Promise<{ url: string; customConfirmation: CustomConfirmation; attendedAt: string; status: MedicalAppointmentStatus }> {
    const res = await api.post(`/medical-appointments/appointments/${id}/create-custom-confirmation`, payload)
    return res.data as {
      url: string
      customConfirmation: CustomConfirmation
      attendedAt: string
      status: MedicalAppointmentStatus
    }
  },

  async customConfirmationPrefill(id: string): Promise<CustomConfirmation | null> {
    const res = await api.get<SingleResponse<{ customConfirmation: CustomConfirmation | null }>>(
      `/medical-appointments/appointments/${id}/custom-confirmation-prefill`,
    )
    return (res.data as unknown as { customConfirmation: CustomConfirmation | null }).customConfirmation
  },
}
